import * as THREE from 'three'
import type { SplatData } from '@/plugins/gsplat/core/SplatData'
import { getLoggerManager } from '@/shared/logging'

const log = getLoggerManager().getLogger('gsplat:tool')

/**
 * 包围盒裁剪区域
 */
export interface CropBox {
  /** 包围盒最小角点（本地坐标） */
  min: THREE.Vector3
  /** 包围盒最大角点（本地坐标） */
  max: THREE.Vector3
}

/**
 * 球体裁剪区域
 */
export interface CropSphere {
  /** 球心世界坐标 */
  center: THREE.Vector3
  /** 球体半径，单位米，>0 */
  radius: number
}

/**
 * Splat 裁剪工具
 *
 * 提供多种裁剪方式（包围盒、球体、平面）来删除或保留特定区域的splat。
 * 使用删除掩码（Uint8Array）标记已删除的splat，避免修改原始数据。
 *
 * 使用方式：
 * ```typescript
 * const cropper = new SplatCropper(splatData);
 *
 * // 按包围盒裁剪
 * const box = {
 *   min: new THREE.Vector3(-1, -1, -1),
 *   max: new THREE.Vector3(1, 1, 1)
 * };
 * const deletedCount = cropper.cropByBox(box, false);
 *
 * // 导出时可使用删除掩码
 * const mask = cropper.getDeletedMask();
 * ```
 */
export class SplatCropper {
  private splatData: SplatData
  private deletedMask: Uint8Array

  /**
   * 构造函数
   * @param splatData Splat数据对象
   */
  constructor(splatData: SplatData) {
    this.splatData = splatData
    this.deletedMask = new Uint8Array(splatData.numSplats)
  }

  /**
   * 按包围盒裁剪
   *
   * @param box 包围盒（世界坐标）
   * @param invert 是否反转：false=删除盒外，true=删除盒内
   * @returns 新删除的splat数量
   */
  cropByBox(box: CropBox, invert: boolean = false): number {
    const x = this.splatData.getProp('x') as Float32Array
    const y = this.splatData.getProp('y') as Float32Array
    const z = this.splatData.getProp('z') as Float32Array

    if (!x || !y || !z) {
      log.warn('SplatCropper: 缺少位置数据')
      return 0
    }

    let count = 0
    for (let i = 0; i < this.splatData.numSplats; i++) {
      // 检查是否在盒内
      const inside =
        x[i] >= box.min.x &&
        x[i] <= box.max.x &&
        y[i] >= box.min.y &&
        y[i] <= box.max.y &&
        z[i] >= box.min.z &&
        z[i] <= box.max.z

      const shouldDelete = invert ? inside : !inside
      if (shouldDelete && !this.deletedMask[i]) {
        this.deletedMask[i] = 1
        count++
      }
    }

    return count
  }

  /**
   * 按球体裁剪
   *
   * @param sphere 球体（世界坐标）
   * @param invert 是否反转：false=删除球外，true=删除球内
   * @returns 新删除的splat数量
   */
  cropBySphere(sphere: CropSphere, invert: boolean = false): number {
    const x = this.splatData.getProp('x') as Float32Array
    const y = this.splatData.getProp('y') as Float32Array
    const z = this.splatData.getProp('z') as Float32Array

    if (!x || !y || !z) {
      log.warn('SplatCropper: 缺少位置数据')
      return 0
    }

    const r2 = sphere.radius * sphere.radius
    let count = 0

    for (let i = 0; i < this.splatData.numSplats; i++) {
      const dx = x[i] - sphere.center.x
      const dy = y[i] - sphere.center.y
      const dz = z[i] - sphere.center.z
      const dist2 = dx * dx + dy * dy + dz * dz

      const inside = dist2 <= r2
      const shouldDelete = invert ? inside : !inside

      if (shouldDelete && !this.deletedMask[i]) {
        this.deletedMask[i] = 1
        count++
      }
    }

    return count
  }

  /**
   * 按平面裁剪
   *
   * @param plane 裁剪平面（世界坐标）
   * @param keepPositiveSide 是否保留正半空间（法线指向的一侧）
   * @returns 新删除的splat数量
   */
  cropByPlane(plane: THREE.Plane, keepPositiveSide: boolean = true): number {
    const x = this.splatData.getProp('x') as Float32Array
    const y = this.splatData.getProp('y') as Float32Array
    const z = this.splatData.getProp('z') as Float32Array

    if (!x || !y || !z) {
      log.warn('SplatCropper: 缺少位置数据')
      return 0
    }

    let count = 0
    const point = new THREE.Vector3()

    for (let i = 0; i < this.splatData.numSplats; i++) {
      point.set(x[i], y[i], z[i])
      const distance = plane.distanceToPoint(point)

      // distance > 0：在平面正侧（法线指向侧）
      // distance < 0：在平面负侧
      const shouldDelete = keepPositiveSide ? distance < 0 : distance >= 0

      if (shouldDelete && !this.deletedMask[i]) {
        this.deletedMask[i] = 1
        count++
      }
    }

    return count
  }

  /**
   * 按索引删除
   *
   * @param indices 要删除的splat索引数组
   */
  deleteByIndices(indices: number[]): void {
    for (const i of indices) {
      if (i >= 0 && i < this.splatData.numSplats) {
        this.deletedMask[i] = 1
      }
    }
  }

  /**
   * 撤销所有删除操作，恢复所有splat
   */
  reset(): void {
    this.deletedMask.fill(0)
  }

  /**
   * 获取删除掩码
   * 注意：返回的是内部数组的引用，修改会影响裁剪器状态
   *
   * @returns 删除掩码（0=有效，1=已删除）
   */
  getDeletedMask(): Uint8Array {
    return this.deletedMask
  }

  /**
   * 获取有效（未删除）的splat索引列表
   *
   * @returns 有效索引的Uint32Array
   */
  getValidIndices(): Uint32Array {
    const indices: number[] = []
    for (let i = 0; i < this.splatData.numSplats; i++) {
      if (!this.deletedMask[i]) {
        indices.push(i)
      }
    }
    return new Uint32Array(indices)
  }

  /**
   * 获取裁剪统计信息
   *
   * @returns 总数、已删除数、剩余数
   */
  getStats(): { total: number; deleted: number; remaining: number } {
    let deleted = 0
    for (let i = 0; i < this.deletedMask.length; i++) {
      if (this.deletedMask[i]) deleted++
    }
    return {
      total: this.splatData.numSplats,
      deleted,
      remaining: this.splatData.numSplats - deleted,
    }
  }
}
